import { useState, useEffect } from 'react'
import { api } from '../lib/api'

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16 }
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 14 }
const subtleBtn = { ...btn, background: '#334155' }
const input = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: 'white' }
const label = { display: 'block', fontSize: 14, color: '#94a3b8', marginBottom: 6 }
const select = { ...input, cursor: 'pointer' }

export function QuizTemplateManager({ quiz, categories = [], vtneDomains = [] }) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [templateType, setTemplateType] = useState('fixed') // 'fixed' or 'rule'
  const [templateName, setTemplateName] = useState('')
  const [templateDesc, setTemplateDesc] = useState('')
  const [ruleType, setRuleType] = useState('random')
  const [ruleCount, setRuleCount] = useState(10)
  const [filterCategoryIds, setFilterCategoryIds] = useState([])
  const [filterVtneDomainIds, setFilterVtneDomainIds] = useState([])
  const [filterStatus, setFilterStatus] = useState('approved')
  const [filterDifficulty, setFilterDifficulty] = useState('')
  const [filterCognitiveLevel, setFilterCognitiveLevel] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    loadTemplates()
  }, [])

  async function loadTemplates() {
    setLoading(true)
    try {
      const data = await api.templates()
      setTemplates(data)
    } catch (err) {
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }

  async function createTemplate() {
    if (!templateName) return
    setSaving(true)
    setMessage('')
    setError('')
    try {
      if (templateType === 'fixed') {
        // Create fixed template from current quiz
        if (!quiz.id) {
          setError('No quiz selected')
          setSaving(false)
          return
        }
        await api.createTemplate(quiz.id, {
          name: templateName,
          description: templateDesc,
          template_type: 'fixed',
        })
      } else {
        // Create rule-based template
        const filters = {}
        if (filterCategoryIds.length > 0) filters.category_ids = filterCategoryIds
        if (filterVtneDomainIds.length > 0) filters.vtne_domain_ids = filterVtneDomainIds
        if (filterStatus) filters.status = filterStatus
        if (filterDifficulty) filters.difficulty = filterDifficulty
        if (filterCognitiveLevel) filters.cognitive_level = filterCognitiveLevel

        const ruleConfig = {
          type: ruleType,
          count: parseInt(ruleCount, 10),
          filters,
        }

        await api.createRuleBasedTemplate({
          name: templateName,
          description: templateDesc,
          template_type: 'rule',
          rule_config: ruleConfig,
        })
      }

      await loadTemplates()
      setTemplateName('')
      setTemplateDesc('')
      setTemplateType('fixed')
      setRuleCount(10)
      setFilterCategoryIds([])
      setFilterVtneDomainIds([])
      setFilterStatus('approved')
      setFilterDifficulty('')
      setFilterCognitiveLevel('')
      setShowCreate(false)
      setMessage('✓ Template saved')
    } catch (err) {
      setError(String(err))
    } finally {
      setSaving(false)
    }
  }

  async function deleteTemplate(templateId) {
    if (!confirm('Delete this template?')) return
    setSaving(true)
    try {
      await api.deleteTemplate(templateId)
      await loadTemplates()
      setMessage('✓ Template deleted')
    } catch (err) {
      setError(String(err))
    } finally {
      setSaving(false)
    }
  }

  async function generateFromTemplate(template) {
    const newTitle = prompt(`Generate new quiz from "${template.name}":`, `${template.name} - Copy`)
    if (!newTitle) return

    setSaving(true)
    try {
      const newQuiz = await api.restoreTemplate(template.id, newTitle)
      setMessage(`✓ Quiz generated: ${newQuiz.title}`)
      // In a real app, you'd navigate to the new quiz
      // For now, just show success
    } catch (err) {
      setError(String(err))
    } finally {
      setSaving(false)
    }
  }

  const toggleCategoryId = (id) => {
    setFilterCategoryIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleVtneDomainId = (id) => {
    setFilterVtneDomainIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  return (
    <div style={box}>
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>Quiz Templates</h3>
      <p style={{ margin: '0 0 16px 0', color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
        Fixed templates save a specific quiz lineup. Rule-based templates generate a fresh quiz from filters, and generated quizzes freeze selected question content into snapshots for stable exports.
      </p>
      {message && <div style={{ color: '#86efac', background: '#052e16', padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 14 }}>{message}</div>}
      {error && <div style={{ color: '#fca5a5', background: '#450a0a', padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 14 }}>{error}</div>}

      {!showCreate && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn} onClick={() => { setShowCreate(true); setTemplateType('fixed'); setMessage(''); }} disabled={!quiz.id}>
            {templates.length > 0 ? '💾 Save as Fixed Template' : '💾 Save as Template'}
          </button>
          <button style={subtleBtn} onClick={() => { setShowCreate(true); setTemplateType('rule'); setMessage(''); }}>
            ⚡ Create Rule-Based Template
          </button>
        </div>
      )}

      {showCreate && (
        <div style={{ marginBottom: 16, padding: 14, background: '#0f172a', borderRadius: 8, border: '1px solid #334155' }}>
          <label style={label}>Template Type</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                value="fixed"
                checked={templateType === 'fixed'}
                onChange={e => setTemplateType(e.target.value)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: 14 }}>Fixed (save current quiz)</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                value="rule"
                checked={templateType === 'rule'}
                onChange={e => setTemplateType(e.target.value)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: 14 }}>Rule-Based (generate by filters)</span>
            </label>
          </div>

          <label style={label}>Template Name *</label>
          <input
            style={input}
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            placeholder="e.g., VTNE Practice Set A"
            autoFocus
          />
          <div style={{ height: 10 }} />

          <label style={label}>Description (optional)</label>
          <textarea
            style={{ ...input, resize: 'vertical', minHeight: 60 }}
            value={templateDesc}
            onChange={e => setTemplateDesc(e.target.value)}
            placeholder="Notes about this template…"
          />

          {templateType === 'rule' && (
            <>
              <div style={{ height: 16 }} />
              <h4 style={{ margin: '0 0 12px 0', color: '#e2e8f0', fontSize: 13 }}>Rule Configuration</h4>

              <label style={label}>Rule Type</label>
              <select
                style={select}
                value={ruleType}
                onChange={e => setRuleType(e.target.value)}
              >
                <option value="random">Random Selection</option>
              </select>
              <div style={{ height: 12 }} />

              <label style={label}>Number of Questions</label>
              <input
                style={input}
                type="number"
                min="1"
                max="100"
                value={ruleCount}
                onChange={e => setRuleCount(e.target.value)}
                placeholder="10"
              />
              <div style={{ height: 12 }} />

              <label style={label}>Filter by Category (optional)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
                {categories.map(cat => (
                  <label key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={filterCategoryIds.includes(cat.id)}
                      onChange={() => toggleCategoryId(cat.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    {cat.name}
                  </label>
                ))}
              </div>

              <label style={label}>Filter by VTNE Domain (optional)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
                {vtneDomains.map(domain => (
                  <label key={domain.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={filterVtneDomainIds.includes(domain.id)}
                      onChange={() => toggleVtneDomainId(domain.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    {domain.name}
                  </label>
                ))}
              </div>

              <label style={label}>Status</label>
              <select
                style={select}
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
              >
                <option value="">Any</option>
                <option value="approved">Approved</option>
                <option value="needs_review">Needs Review</option>
                <option value="draft">Draft</option>
              </select>
              <div style={{ height: 12 }} />

              <label style={label}>Difficulty (optional)</label>
              <select
                style={select}
                value={filterDifficulty}
                onChange={e => setFilterDifficulty(e.target.value)}
              >
                <option value="">Any</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
              <div style={{ height: 12 }} />

              <label style={label}>Cognitive Level (optional)</label>
              <select
                style={select}
                value={filterCognitiveLevel}
                onChange={e => setFilterCognitiveLevel(e.target.value)}
              >
                <option value="">Any</option>
                <option value="recall">Recall</option>
                <option value="understanding">Understanding</option>
                <option value="application">Application</option>
                <option value="analysis">Analysis</option>
              </select>
            </>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button style={btn} onClick={createTemplate} disabled={!templateName || saving}>
              {saving ? 'Saving…' : 'Save Template'}
            </button>
            <button style={subtleBtn} onClick={() => { setShowCreate(false); setMessage(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {templates.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, fontWeight: 500 }}>Saved templates ({templates.length})</div>
          {templates.map(template => (
            <div key={template.id} style={{ padding: 12, background: '#0f172a', borderRadius: 6, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #334155' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {template.name}
                  <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                    ({template.template_type === 'rule' ? '⚡ Rule-Based' : '📋 Fixed'})
                  </span>
                </div>
                {template.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{template.description}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  style={{ ...btn, padding: '6px 12px', fontSize: 12 }}
                  onClick={() => generateFromTemplate(template)}
                  disabled={saving}
                >
                  Generate
                </button>
                <button
                  style={{ ...subtleBtn, padding: '6px 12px', fontSize: 12 }}
                  onClick={() => deleteTemplate(template.id)}
                  disabled={saving}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!templates.length && !showCreate && quiz.id && (
        <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>No templates yet. Save this quiz as a template to quickly create similar quizzes later.</p>
      )}
    </div>
  )
}
